import { z } from 'zod';

import { LOCALES, type Locale } from '../locale/locale';

/**
 * Wraps a per-locale field schema in the translations map every translatable
 * entity carries (N5, W7): one entry per supported locale, **all required**, so
 * a role/question/course cannot be created with Arabic filled in and English
 * blank — U8's key-parity law applied to database content.
 *
 * Single-item reads return the same shape, which is what the admin edit form's
 * per-locale tabs prefill from.
 *
 * @example
 * const RoleTranslationsSchema = makeTranslationsSchema(
 *   z.object({ name: z.string().min(1) }),
 * );
 */
export function makeTranslationsSchema<TItem extends z.ZodType>(item: TItem) {
  // Written as a literal rather than built from LOCALES so the inferred type is
  // exact; `translations.spec.ts` asserts the keys still equal LOCALES, so
  // adding a locale fails a test instead of silently skipping a language.
  return z.object({ ar: item, en: item }).strict();
}

/** The runtime value of a translations map, for callers building one by hand. */
export type Translations<TItem> = Record<Locale, TItem>;

/** Every supported locale, for iterating a translations map without a literal. */
export const TRANSLATION_LOCALES: readonly Locale[] = LOCALES;
